import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Panda — a storybook giant panda sitting on its round bottom, cradling a
// bamboo stalk in its right paw. Chunky toy proportions: an oversized head on
// a plump cream body, stubby splayed legs, big soft eye patches around big
// friendly eyes. Built to animate: the head, the right arm and the bamboo
// stalk are named so the "bamboo-chew" loop can lift the stalk to the mouth
// while the head nibbles.
//
// Frame: faces +Z, sits on the floor (y = 0). About 0.8 m tall.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"   : the torso, shoulder band, left arm and legs. Breathes.
//   - "head"   : the whole head (ears, eye patches, eyes, muzzle). Pivot at
//                the neck so nods read as nibbles, not floor rotations.
//   - "arm-r"  : the right arm + paw. Pivot at the right shoulder.
//   - "bamboo" : the stalk + leaves, held in the right paw. Shares the
//                shoulder pivot with "arm-r" so both swing as one.

const CREAM = "#f6efe3";       // the panda's white — warm cream, never pure white
const CREAM_SHADE = "#e4dac6"; // shaded cream for the belly mass
const DARK = "#3a3138";        // the panda's black — warm charcoal, never pure black
const DARK_SHADE = "#2c252c";  // deeper charcoal for pads and pupils
const BAMBOO = "#7cb246";      // fresh bamboo green
const BAMBOO_NODE = "#5a8f31"; // darker rings at the stalk nodes
const LEAF = "#8fc857";        // young leaves at the tip
const EYE_WHITE = "#faf6ec";   // eye whites (soft, not pure white)

// The pivots the animations rotate the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.26, 0];
const NECK_PIVOT: [number, number, number] = [0, 0.50, 0.02];
const SHOULDER_R: [number, number, number] = [0.20, 0.42, 0.05];

// One big friendly eye: dark patch, white ball, pupil, tiny highlight.
function eye(x: number): Part[] {
  return [
    // The soft dark eye patch the eye sits in.
    { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: DARK,
      position: [x, 0.645, 0.165], scale: [1.0, 1.35, 0.5], rotation: [0, 0, Math.sign(x) * 0.35], name: "head" },
    // Eye white.
    { geometry: new THREE.SphereGeometry(0.033, 10, 8), color: EYE_WHITE,
      position: [x, 0.65, 0.196], name: "head" },
    // Big dark pupil, slightly forward.
    { geometry: new THREE.SphereGeometry(0.021, 8, 6), color: DARK_SHADE,
      position: [x, 0.648, 0.218], name: "head" },
    // Tiny catch-light.
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: EYE_WHITE,
      position: [x + 0.008, 0.658, 0.234], name: "head" },
  ];
}

// One stubby sitting leg sticking forward, with a cream sole facing up.
function leg(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.105, 10, 8), color: DARK,
      position: [x, 0.10, 0.15], scale: [0.9, 0.8, 1.5], name: "body" },
    { geometry: new THREE.SphereGeometry(0.055, 8, 6), color: CREAM_SHADE,
      position: [x, 0.135, 0.29], scale: [1.0, 0.5, 1.0], rotation: [0.5, 0, 0], name: "body" },
  ];
}

export const panda = defineModel({
  id: "panda",
  batchId: "mess",
  name: "Panda",
  tags: ["mess", "cute", "animated", "creature", "animal", "panda", "bear", "bamboo", "mascot"],
  summary: "A plump sitting giant panda cradling a bamboo stalk, ready to nibble.",
  description:
    "A storybook giant panda sitting on its round bottom: an oversized cream " +
    "head with charcoal ears and soft eye patches, a plump body with a dark " +
    "shoulder band, stubby splayed legs and a fresh bamboo stalk held in the " +
    "right paw. The head, right arm and stalk are separately posable; the " +
    "bamboo-chew loop lifts the stalk to the mouth while the head nibbles.",
  author: "models",
  footprint: 0.45,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── torso ──────────────────────────────────────────────────────────
      // Plump cream body, bottom squashed onto the floor.
      { geometry: new THREE.SphereGeometry(0.26, 12, 10), color: CREAM,
        position: [0, 0.26, 0], scale: [1.05, 1.0, 0.95], name: "body" },
      // Rounded belly mass, a shade darker so the front reads.
      { geometry: new THREE.SphereGeometry(0.17, 10, 8), color: CREAM_SHADE,
        position: [0, 0.24, 0.11], scale: [1.05, 1.0, 0.8], name: "body" },
      // Dark shoulder band wrapping the top of the torso (a panda's collar).
      { geometry: new THREE.SphereGeometry(0.19, 12, 10), color: DARK,
        position: [0, 0.44, -0.01], scale: [1.35, 0.5, 1.05], name: "body" },
      // ── legs (sitting, splayed forward) ────────────────────────────────
      ...leg(-0.17),
      ...leg(0.17),
      // ── left arm, resting against the belly ────────────────────────────
      { geometry: new THREE.SphereGeometry(0.085, 10, 8), color: DARK,
        position: [-0.23, 0.35, 0.06], scale: [0.85, 1.5, 0.85], rotation: [0.25, 0, -0.3], name: "body" },
      { geometry: new THREE.SphereGeometry(0.06, 8, 6), color: DARK_SHADE,
        position: [-0.26, 0.24, 0.11], name: "body" },
      // ── right arm, raised to cradle the bamboo (posable) ───────────────
      { geometry: new THREE.SphereGeometry(0.085, 10, 8), color: DARK,
        position: [0.22, 0.35, 0.11], scale: [0.85, 1.45, 0.85], rotation: [-0.35, 0, 0.3], name: "arm-r" },
      // Paw wrapped around the stalk.
      { geometry: new THREE.SphereGeometry(0.062, 8, 6), color: DARK_SHADE,
        position: [0.25, 0.28, 0.19], name: "arm-r" },
      // ── head ───────────────────────────────────────────────────────────
      // Big round head — the panda is barely two heads tall.
      { geometry: new THREE.SphereGeometry(0.20, 14, 12), color: CREAM,
        position: [0, 0.62, 0.02], scale: [1.1, 0.95, 0.95], name: "head" },
      // Round charcoal ears.
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: DARK,
        position: [-0.155, 0.775, -0.01], scale: [1.0, 1.0, 0.7], name: "head" },
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: DARK,
        position: [0.155, 0.775, -0.01], scale: [1.0, 1.0, 0.7], name: "head" },
      ...eye(-0.088),
      ...eye(0.088),
      // Muzzle + nose.
      { geometry: new THREE.SphereGeometry(0.07, 10, 8), color: CREAM,
        position: [0, 0.565, 0.185], scale: [1.1, 0.75, 0.8], name: "head" },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: DARK_SHADE,
        position: [0, 0.59, 0.24], scale: [1.2, 0.85, 0.8], name: "head" },
      // ── bamboo stalk (held in the right paw, posable with the arm) ─────
      { geometry: new THREE.CylinderGeometry(0.018, 0.021, 0.42, 8), color: BAMBOO,
        position: [0.25, 0.37, 0.20], name: "bamboo" },
      // Node rings along the stalk.
      { geometry: new THREE.CylinderGeometry(0.023, 0.023, 0.016, 8), color: BAMBOO_NODE,
        position: [0.25, 0.28, 0.20], name: "bamboo" },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.016, 8), color: BAMBOO_NODE,
        position: [0.25, 0.46, 0.20], name: "bamboo" },
      // Two young leaves at the tip.
      { geometry: new THREE.ConeGeometry(0.022, 0.11, 5), color: LEAF,
        position: [0.28, 0.60, 0.21], rotation: [0.15, 0, -0.7], name: "bamboo" },
      { geometry: new THREE.ConeGeometry(0.02, 0.09, 5), color: LEAF,
        position: [0.22, 0.61, 0.19], rotation: [-0.1, 0, 0.6], name: "bamboo" },
    ];
  },
  animations: [
    // 1. Idle — a slow breath: the body swells a touch, the head rises in
    //    sympathy and rocks a hair. Never looks dead.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.4,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 1.7, value: 1.025, ease: "sine" },
            { t: 3.4, value: 1, ease: "sine" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0.02], ease: "sine" },
            { t: 1.7, value: [0.03, 0, -0.02], ease: "sine" },
            { t: 3.4, value: [0, 0, 0.02], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [-0.05, 0, 0.03], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "bamboo", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [-0.05, 0, 0.03], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Bamboo chew — the arm swings the stalk up toward the mouth, the head
    //    dips to meet it and nibbles in quick little bobs, then the stalk
    //    settles back down. The arm and stalk share the shoulder pivot so the
    //    paw never lets go.
    {
      id: "bamboo-chew",
      label: "Bamboo chew",
      duration: 4.0,
      loop: true,
      tracks: [
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [-0.35, 0, 0.85], ease: "easeInOut" },
            { t: 3.0, value: [-0.35, 0, 0.85], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "bamboo", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [-0.35, 0, 0.85], ease: "easeInOut" },
            { t: 3.0, value: [-0.35, 0, 0.85], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // The head leans toward the raised tip and nibbles.
          target: "head", pivot: NECK_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.9, value: [0.22, 0.15, -0.1], ease: "easeInOut" },
            { t: 1.3, value: [0.3, 0.15, -0.1], ease: "easeInOut" },
            { t: 1.7, value: [0.2, 0.15, -0.1], ease: "easeInOut" },
            { t: 2.1, value: [0.3, 0.15, -0.1], ease: "easeInOut" },
            { t: 2.5, value: [0.2, 0.15, -0.1], ease: "easeInOut" },
            { t: 2.9, value: [0.28, 0.15, -0.1], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
